import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { videosAPI } from '../services/videosApi';
import { getApiErrorMessage } from '../utils/apiError';

// Deleting a video is permanent (backend/app/api/v1/uploads.py DELETE removes its runs,
// findings, report and stored files), so the dialog names the item and says so plainly. The
// Videos list offers it so an unfinished upload or photo set can be cleared (2026-09-26).
export default function DeleteVideoModal({ video, projectId, onClose }) {
  const queryClient = useQueryClient();
  const isPhotoSet = video?.kind === 'photos';

  const deleteMutation = useMutation(() => videosAPI.remove(video.id), {
    onSuccess: () => {
      toast.success(isPhotoSet ? 'Photo set deleted' : 'Video deleted');
      queryClient.invalidateQueries(['project-videos', projectId]);
      onClose?.();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not delete it'));
    },
  });

  if (!video) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" aria-hidden="true" />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <div
          className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-video-modal-title"
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-red-100">
              <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 id="delete-video-modal-title" className="text-lg leading-6 font-medium text-gray-900">
                {isPhotoSet ? 'Delete photo set' : 'Delete video'}
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Delete <strong>{video.title}</strong>? This permanently removes{' '}
                {isPhotoSet ? 'the photos' : 'the video'}, its runs, findings and report, and its stored
                files. This cannot be undone.
              </p>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
